import { RotateCcw } from "lucide-react";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { useEmploymentTypeOptions } from "@/hooks/use-employment-types";
import type { EmploymentTypeFilter, OrgHealthFilters } from "@/hooks/use-org-health";
import { cn } from "@/lib/utils";

/**
 * The Org Health filter row — pillar / department / role comboboxes plus
 * employment-type include/exclude chips (all lit = no filter; toggling one off
 * excludes that employment type from every widget). One row above the charts.
 */
export function OrgHealthFilterBar({
  filters,
  onChange,
}: {
  filters: OrgHealthFilters;
  onChange: (next: OrgHealthFilters) => void;
}) {
  // Chips come from the admin-managed employment-type list (EPIC-028), so a
  // type added in Settings appears here without a deploy.
  const employmentChips = useEmploymentTypeOptions().options;

  // Empty selection means "all included"; a chip is lit unless a non-empty
  // selection exists that omits it.
  const included = (t: EmploymentTypeFilter) =>
    filters.employmentTypes.length === 0 || filters.employmentTypes.includes(t);

  function toggleEmploymentType(t: EmploymentTypeFilter) {
    const current = new Set(
      filters.employmentTypes.length === 0
        ? employmentChips.map((c) => c.value)
        : filters.employmentTypes,
    );
    if (current.has(t)) current.delete(t);
    else current.add(t);
    // Excluding everything shows nothing useful — treat as back to "all".
    const next = current.size === 0 || current.size === employmentChips.length ? [] : [...current];
    onChange({ ...filters, employmentTypes: next });
  }

  const hasAnyFilter =
    filters.pillarId !== null ||
    filters.departmentId !== null ||
    filters.roleId !== null ||
    filters.employmentTypes.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <EntityCombobox
        entity="pillars"
        size="sm"
        className="w-44"
        value={filters.pillarId}
        includeNone
        noneLabel="All pillars"
        placeholder="All pillars"
        // Changing pillar invalidates a narrower department pick.
        onChange={(pillarId) => onChange({ ...filters, pillarId, departmentId: null })}
      />
      <EntityCombobox
        entity="departments"
        size="sm"
        className="w-48"
        value={filters.departmentId}
        includeNone
        noneLabel="All departments"
        placeholder="All departments"
        filters={filters.pillarId ? { pillarId: filters.pillarId } : undefined}
        onChange={(departmentId) => onChange({ ...filters, departmentId })}
      />
      <EntityCombobox
        entity="roles"
        size="sm"
        className="w-48"
        value={filters.roleId}
        includeNone
        noneLabel="All roles"
        placeholder="All roles"
        onChange={(roleId) => onChange({ ...filters, roleId })}
      />

      <fieldset className="flex items-center gap-1" aria-label="Employment types">
        {employmentChips.map((chip) => {
          const on = included(chip.value);
          return (
            <button
              key={chip.value}
              type="button"
              aria-pressed={on}
              onClick={() => toggleEmploymentType(chip.value)}
              className={cn(
                "rounded-full border px-3 py-1 font-medium text-xs transition-colors",
                on
                  ? "border-primary/30 bg-primary/10 text-primary"
                  : "bg-card text-muted-foreground line-through hover:text-foreground",
              )}
            >
              {chip.label}
            </button>
          );
        })}
      </fieldset>

      {hasAnyFilter && (
        <button
          type="button"
          onClick={() =>
            onChange({ pillarId: null, departmentId: null, roleId: null, employmentTypes: [] })
          }
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
        >
          <RotateCcw className="size-3" />
          Reset filters
        </button>
      )}
    </div>
  );
}
